import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { playArcadeSound, unlockArcadeAudio } from '../../shared/audio/audio';
import { readMaxScore, writeMaxScore } from '../../shared/storage/localRecords';
import { trackEvent } from '../../app/telemetry';
import {
  createTriviaRound,
  getTriviaCategories,
} from './triviaQuestionBank';
import {
  MIXED_CATEGORY_ID,
  applyAnswerToSummary,
  calculateTriviaScore,
  createEmptyTriviaSummary,
  evaluateAnswer,
  isTriviaRecord,
} from './triviaLogic';
import { triviaDefinition } from './triviaDefinition';
import './TriviaGame.css';

const REVEAL_AUTO_ADVANCE_MS = 3000;
const CATEGORY_ACCENTS = {
  history: '#ffb14a',
  geography: '#57d8ff',
  mythology: '#c792ff',
  literature: '#7af7b2',
  'science-nature': '#89e85e',
  general: '#ffd166',
  'pop-culture': '#ff7ab6',
  anime: '#ff9fda',
  'video-games': '#7cddff',
  cinema: '#ff657a',
  music: '#8fd8ff',
  'sports-games': '#f4e76e',
  mixed: '#ffffff',
};
const CATEGORY_ICONS = {
  history: 'HI',
  geography: 'GE',
  mythology: 'MY',
  literature: 'LI',
  'science-nature': 'SN',
  general: 'GN',
  'pop-culture': 'PC',
  anime: 'AN',
  'video-games': 'VG',
  cinema: 'CI',
  music: 'MU',
  'sports-games': 'SG',
  mixed: 'MX',
};

function getCategoryLabel(categories, categoryId) {
  if (categoryId === MIXED_CATEGORY_ID) {
    return 'Mixed';
  }

  return categories.find((category) => category.id === categoryId)?.label ?? categoryId;
}

function TriviaGame({
  definition = triviaDefinition,
  onReturnToLibrary,
}) {
  const categories = useMemo(() => getTriviaCategories(), []);
  const categoryChoices = useMemo(() => [
    {
      id: MIXED_CATEGORY_ID,
      label: 'Mixed',
      description: 'A little bit of everything.',
    },
    ...categories,
  ], [categories]);
  const [phase, setPhase] = useState('category');
  const [round, setRound] = useState([]);
  const [categoryId, setCategoryId] = useState(MIXED_CATEGORY_ID);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [summary, setSummary] = useState(() => createEmptyTriviaSummary(MIXED_CATEGORY_ID));
  const [answerResult, setAnswerResult] = useState(null);
  const [maxScore, setMaxScore] = useState(() =>
    readMaxScore(window.localStorage, definition.storageNamespace)
  );
  const [lastRoundWasRecord, setLastRoundWasRecord] = useState(false);
  const revealAdvanceTimerRef = useRef(null);
  const activeQuestion = round[questionIndex] ?? null;
  const categoryLabel = getCategoryLabel(categories, categoryId);
  const isLastQuestion = questionIndex >= round.length - 1;

  const clearRevealAdvanceTimer = useCallback(() => {
    if (revealAdvanceTimerRef.current) {
      window.clearTimeout(revealAdvanceTimerRef.current);
      revealAdvanceTimerRef.current = null;
    }
  }, []);

  const finishRound = useCallback((nextSummary) => {
    clearRevealAdvanceTimer();
    setPhase('summary');
    const nextIsRecord = isTriviaRecord(nextSummary.score, maxScore);

    setLastRoundWasRecord(nextIsRecord);

    if (nextIsRecord) {
      writeMaxScore(nextSummary.score, window.localStorage, definition.storageNamespace);
      setMaxScore(nextSummary.score);
    }

    trackEvent('arcade_trivia.round_completed', {
      gameId: definition.id,
      categoryId: nextSummary.categoryId,
      score: nextSummary.score,
      accuracy: nextSummary.accuracy,
      bestStreak: nextSummary.bestStreak,
    });
  }, [clearRevealAdvanceTimer, definition.id, definition.storageNamespace, maxScore]);

  const advanceQuestion = useCallback((nextSummary = summary) => {
    clearRevealAdvanceTimer();

    if (isLastQuestion) {
      finishRound(nextSummary);
      return;
    }

    setQuestionIndex((current) => current + 1);
    setAnswerResult(null);
    setPhase('active');
  }, [clearRevealAdvanceTimer, finishRound, isLastQuestion, summary]);

  const revealAnswer = useCallback((answerId = null) => {
    if (phase !== 'active' || !activeQuestion) {
      return;
    }

    const result = evaluateAnswer(activeQuestion, answerId);
    const nextStreak = result.isCorrect ? summary.currentStreak + 1 : 0;
    const nextScoreResult = calculateTriviaScore({
      isCorrect: result.isCorrect,
      remainingMs: 0,
      questionMs: 1,
      streak: nextStreak,
    });
    const nextSummary = applyAnswerToSummary(summary, result, nextScoreResult);

    setPhase('reveal');
    setAnswerResult(result);
    setSummary(nextSummary);
    playArcadeSound(result.isCorrect ? 'success' : 'hit');
    revealAdvanceTimerRef.current = window.setTimeout(() => {
      advanceQuestion(nextSummary);
    }, REVEAL_AUTO_ADVANCE_MS);
  }, [activeQuestion, advanceQuestion, phase, summary]);

  const startRound = useCallback((nextCategoryId) => {
    unlockArcadeAudio();
    clearRevealAdvanceTimer();
    const seed = `${nextCategoryId}:${Date.now()}`;
    const nextRound = createTriviaRound({
      categoryId: nextCategoryId,
      count: definition.roundQuestionCount,
      seed,
    });

    setRound(nextRound);
    setCategoryId(nextCategoryId);
    setQuestionIndex(0);
    setAnswerResult(null);
    setSummary(createEmptyTriviaSummary(nextCategoryId));
    setLastRoundWasRecord(false);
    setPhase('active');
    playArcadeSound('start');
    trackEvent('arcade_trivia.round_started', {
      gameId: definition.id,
      categoryId: nextCategoryId,
      questionCount: nextRound.length,
    });
  }, [clearRevealAdvanceTimer, definition.id, definition.roundQuestionCount]);

  const restartRound = useCallback(() => {
    startRound(categoryId);
  }, [categoryId, startRound]);

  const returnToCategories = useCallback(() => {
    clearRevealAdvanceTimer();
    setPhase('category');
    setAnswerResult(null);
  }, [clearRevealAdvanceTimer]);

  useEffect(() => () => {
    clearRevealAdvanceTimer();
  }, [clearRevealAdvanceTimer]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (phase === 'category' && event.key === 'Escape') {
        onReturnToLibrary?.();
        return;
      }

      if (phase === 'reveal' && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        advanceQuestion();
        return;
      }

      if (phase !== 'active' || !activeQuestion) {
        return;
      }

      const answerIndex = ['1', '2', '3', '4'].indexOf(event.key);

      if (answerIndex >= 0) {
        event.preventDefault();
        revealAnswer(activeQuestion.answers[answerIndex]?.id);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeQuestion, advanceQuestion, onReturnToLibrary, phase, revealAnswer]);

  return (
    <main className="game-app trivia-app">
      <section
        className="trivia-shell"
        aria-label="Arcade Trivia"
        style={{ '--trivia-accent': CATEGORY_ACCENTS[categoryId] ?? CATEGORY_ACCENTS.mixed }}
      >
        <header className="trivia-topbar">
          <button className="trivia-icon-button" type="button" onClick={onReturnToLibrary}>
            Menu
          </button>
          <div className="trivia-brand">
            <h1>{phase === 'category' ? 'Choose a Category' : categoryLabel}</h1>
          </div>
          <div className="trivia-header-stat" aria-label={phase === 'category' ? `Best score ${maxScore}` : `Question ${questionIndex + 1} of ${round.length}`}>
            <span>{phase === 'category' ? 'Best' : `${questionIndex + 1}/${round.length}`}</span>
            {phase === 'category' ? <strong>{maxScore}</strong> : null}
          </div>
        </header>

        {phase === 'category' ? (
          <div className="trivia-category-screen">
            <div className="trivia-category-grid" aria-label="Trivia categories">
              {categoryChoices.map((category) => (
                <button
                  className="trivia-category-card"
                  key={category.id}
                  onClick={() => startRound(category.id)}
                  style={{ '--trivia-card-accent': CATEGORY_ACCENTS[category.id] ?? CATEGORY_ACCENTS.mixed }}
                  type="button"
                >
                  <span className="trivia-category-icon" aria-hidden="true" data-testid="trivia-category-icon">
                    {CATEGORY_ICONS[category.id] ?? '??'}
                  </span>
                  <span className="trivia-category-copy">
                    <strong>{category.label}</strong>
                    <span>{category.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {phase === 'active' || phase === 'reveal' ? (
          <div className={`trivia-play trivia-play-${phase}`}>
            <div className="trivia-hud" aria-label="Round status">
              <span>Score {summary.score}</span>
              <span>Streak {summary.currentStreak}</span>
            </div>

            <article className="trivia-question-panel">
              <div className="trivia-question-meta">
                <span>{categoryLabel}</span>
                <span>{activeQuestion?.difficulty}</span>
              </div>
              <h2>{activeQuestion?.question}</h2>
            </article>

            <div className="trivia-answer-grid" aria-label="Answer choices">
              {activeQuestion?.answers.map((answer, index) => {
                const isSelected = answerResult?.selectedAnswerId === answer.id;
                const isCorrect = answerResult?.correctAnswerId === answer.id;
                const stateClass = phase === 'reveal' && isCorrect
                  ? 'trivia-answer-correct'
                  : phase === 'reveal' && isSelected
                    ? 'trivia-answer-wrong'
                    : '';

                return (
                  <button
                    aria-label={`Answer ${index + 1}: ${answer.text}${phase === 'reveal' && isCorrect ? ', correct answer' : ''}`}
                    className={`trivia-answer ${stateClass}`}
                    disabled={phase !== 'active'}
                    key={answer.id}
                    onClick={() => revealAnswer(answer.id)}
                    type="button"
                  >
                    <span>{index + 1}</span>
                    <strong>{answer.text}</strong>
                  </button>
                );
              })}
            </div>

            {phase === 'reveal' ? (
              <div
                aria-label={answerResult?.isCorrect ? 'Correct answer selected' : 'Answer revealed'}
                className="trivia-reveal"
                role="status"
                aria-live="polite"
              >
                <button type="button" onClick={() => advanceQuestion()}>
                  {isLastQuestion ? 'Results' : 'Next'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {phase === 'summary' ? (
          <div className="trivia-summary" role="status" aria-live="polite">
            <div className="trivia-summary-score">
              <span>{categoryLabel}</span>
              <strong>{summary.score}</strong>
              <p>{lastRoundWasRecord ? 'New best' : 'Round score'}</p>
            </div>
            <div className="trivia-summary-stats">
              <span><strong>{summary.accuracy}%</strong> Accuracy</span>
              <span><strong>{summary.correct}/{summary.answered}</strong> Correct</span>
              <span><strong>{summary.bestStreak}</strong> Best streak</span>
            </div>
            <div className="trivia-summary-actions">
              <button type="button" onClick={restartRound}>Retry</button>
              <button type="button" onClick={returnToCategories}>Categories</button>
              <button type="button" onClick={onReturnToLibrary}>Game Menu</button>
            </div>
          </div>
        ) : null}
      </section>
    </main>
  );
}

export default TriviaGame;
